import React, { useMemo, useState } from "react";
import { Navbar, Footer } from "../../Components";
import { useSubmitValuationRequestMutation } from "../../Services/AdvertiseService";
import "./style.css";

const formatEur = (value) => `${Math.round(value).toLocaleString("de-DE")} EUR`;
const formatPct = (value) => `${Number(value).toFixed(1).replace(".", ",")} %`;

export default function RechnerPage() {
  const [netto, setNetto] = useState(3500);
  const [extra, setExtra] = useState(0);
  const [mieteAktuell, setMieteAktuell] = useState(800);
  const [kreditrate, setKreditrate] = useState(0);
  const [leben, setLeben] = useState(1500);
  const [eigenkapital, setEigenkapital] = useState(30000);
  const [sparrate, setSparrate] = useState(200);
  const [zins, setZins] = useState(3.5);
  const [tilgung, setTilgung] = useState(2.0);
  const [laufzeit] = useState(25);
  const [jahresmiete, setJahresmiete] = useState(0);

  const [showForm, setShowForm] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const [submitValuationRequest, { isLoading }] = useSubmitValuationRequestMutation();

  const results = useMemo(() => {
    const gesamtEinkommen = netto + Number(extra || 0);
    const freieRate = Math.max(0, gesamtEinkommen * 0.35 - kreditrate);
    const monatsrate = (zins + tilgung) / 100 / 12;
    const darlehen = monatsrate > 0 ? Math.round(freieRate / monatsrate) : 0;
    const gesamtBudget = darlehen + eigenkapital;
    const nebenkosten = Math.round(gesamtBudget * 0.1);
    const ekNachNk = eigenkapital - nebenkosten;
    const monatRate = Math.round(darlehen * monatsrate);
    const ekPct = gesamtBudget > 0 ? (eigenkapital / gesamtBudget) * 100 : 0;

    const zinsAnteilMonat = Math.round((darlehen * zins) / 100 / 12);
    const tilgAnteilMonat = Math.max(0, monatRate - zinsAnteilMonat);
    const kapital10J = tilgAnteilMonat * 12 * 10;

    const nettoMehr = monatRate - mieteAktuell;

    const monatMiete = Number(jahresmiete || 0) / 12;
    const rendite = gesamtBudget > 0 ? (Number(jahresmiete || 0) / gesamtBudget) * 100 : 0;
    const cashflow = monatMiete - monatRate;

    return {
      freieRate,
      darlehen,
      gesamtBudget,
      nebenkosten,
      ekNachNk,
      monatRate,
      ekPct,
      nettoMehr,
      kapital10J,
      monatMiete,
      rendite,
      cashflow,
      gesamtEinkommen,
      sparrate,
      laufzeit,
    };
  }, [netto, extra, kreditrate, zins, tilgung, eigenkapital, jahresmiete, mieteAktuell, sparrate, laufzeit]);

  const submitContact = async () => {
    setSubmitError("");

    if (!firstName.trim() || !email.includes("@")) {
      setSubmitError("Bitte mindestens Vorname und gueltige E-Mail angeben.");
      return;
    }

    try {
      await submitValuationRequest({
        propertyType: "Leistbarkeitsrechner Beratung",
        postalCode: "nicht angegeben",
        city: "nicht angegeben",
        timeline: "Beratung gewünscht",
        ownership: "nicht angegeben",
        firstName: firstName.trim(),
        lastName: lastName.trim() || "-",
        email: email.trim(),
        phone: phone.trim(),
        details: {
          rechner: {
            netto,
            extra,
            mieteAktuell,
            kreditrate,
            leben,
            eigenkapital,
            sparrate,
            zins,
            tilgung,
            laufzeit,
            jahresmiete,
          },
          ergebnis: results,
        },
      }).unwrap();

      setSubmitSuccess(true);
    } catch (error) {
      const message = error?.data?.message || "Anfrage konnte nicht gesendet werden.";
      setSubmitError(Array.isArray(message) ? message.join(" ") : message);
    }
  };

  return (
    <>
      <Navbar />
      <main className="calc-page">
        <section className="calc-head">
          <h1>Leistbarkeitsrechner</h1>
          <p>Geben Sie Ihre Daten ein und sehen Sie sofort ein realistisches Immobilienbudget.</p>
        </section>

        <section className="calc-grid">
          <div className="calc-left">
            <div className="calc-block">
              <h2>Einnahmen</h2>
              <label>Netto-Monatseinkommen: {formatEur(netto)}</label>
              <input type="range" min="1000" max="15000" step="250" value={netto} onChange={(e) => setNetto(Number(e.target.value))} />
              <label>Weitere Einnahmen</label>
              <input className="field" type="number" min="0" value={extra} onChange={(e) => setExtra(Number(e.target.value || 0))} />
            </div>

            <div className="calc-block">
              <h2>Ausgaben</h2>
              <label>Warmmiete aktuell: {formatEur(mieteAktuell)}</label>
              <input type="range" min="0" max="3000" step="50" value={mieteAktuell} onChange={(e) => setMieteAktuell(Number(e.target.value))} />
              <label>Bestehende Kreditraten: {formatEur(kreditrate)}</label>
              <input type="range" min="0" max="3000" step="50" value={kreditrate} onChange={(e) => setKreditrate(Number(e.target.value))} />
              <label>Lebenshaltungskosten: {formatEur(leben)}</label>
              <input type="range" min="500" max="4000" step="100" value={leben} onChange={(e) => setLeben(Number(e.target.value))} />
            </div>

            <div className="calc-block">
              <h2>Eigenkapital und Finanzierung</h2>
              <label>Verfügbares Eigenkapital: {formatEur(eigenkapital)}</label>
              <input type="range" min="0" max="500000" step="5000" value={eigenkapital} onChange={(e) => setEigenkapital(Number(e.target.value))} />
              <label>Monatliche Sparrate: {formatEur(sparrate)}</label>
              <input type="range" min="0" max="2000" step="50" value={sparrate} onChange={(e) => setSparrate(Number(e.target.value))} />
              <label>Zins p.a.: {formatPct(zins)}</label>
              <input type="range" min="2" max="6" step="0.1" value={zins} onChange={(e) => setZins(Number(e.target.value))} />
              <label>Tilgung p.a.: {formatPct(tilgung)}</label>
              <input type="range" min="1" max="4" step="0.1" value={tilgung} onChange={(e) => setTilgung(Number(e.target.value))} />
              <label>Erwartete Jahreskaltmiete (Anleger)</label>
              <input className="field" type="number" min="0" value={jahresmiete} onChange={(e) => setJahresmiete(Number(e.target.value || 0))} />
            </div>
          </div>

          <aside className="calc-right">
            <div className="budget-box">
              <p>Maximales Immobilienbudget</p>
              <h3>{formatEur(results.gesamtBudget)}</h3>
              <span>Darlehen {formatEur(results.darlehen)} + Eigenkapital {formatEur(eigenkapital)}</span>
            </div>

            <div className="result-card">
              <h4>Finanzierungsübersicht</h4>
              <div><span>Freie Rate</span><strong>{formatEur(results.freieRate)} / Monat</strong></div>
              <div><span>Kaufnebenkosten (ca. 10%)</span><strong>{formatEur(results.nebenkosten)}</strong></div>
              <div><span>Eigenkapital nach Nebenkosten</span><strong>{formatEur(Math.max(0, results.ekNachNk))}</strong></div>
              <div><span>Monatliche Kreditrate</span><strong>{formatEur(results.monatRate)} / Monat</strong></div>
              <div><span>EK-Anteil</span><strong>{formatPct(results.ekPct)}</strong></div>
            </div>

            <div className="result-card">
              <h4>Eigennutzer</h4>
              <div><span>Netto-Mehrbelastung</span><strong>{formatEur(results.nettoMehr)} / Monat</strong></div>
              <div><span>Aufgebautes EK in 10 Jahren</span><strong>{formatEur(results.kapital10J)}</strong></div>
            </div>

            <div className="result-card">
              <h4>Kapitalanleger</h4>
              <div><span>Monatliche Mieteinnahme</span><strong>{formatEur(results.monatMiete)}</strong></div>
              <div><span>Bruttomietrendite</span><strong>{formatPct(results.rendite)}</strong></div>
              <div><span>Monatlicher Cashflow</span><strong>{formatEur(results.cashflow)}</strong></div>
            </div>

            <button className="cta-btn" onClick={() => setShowForm((prev) => !prev)}>
              Jetzt persönliche Beratung anfragen
            </button>

            {showForm && (
              <div className="contact-form">
                <h4>Persönliche Beratung</h4>
                <div className="row2">
                  <input className="field" placeholder="Vorname" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
                  <input className="field" placeholder="Nachname" value={lastName} onChange={(e) => setLastName(e.target.value)} />
                </div>
                <input className="field" placeholder="E-Mail" value={email} onChange={(e) => setEmail(e.target.value)} />
                <input className="field" placeholder="Telefon (optional)" value={phone} onChange={(e) => setPhone(e.target.value)} />
                {submitError && <p className="err">{submitError}</p>}
                {submitSuccess ? (
                  <p className="ok">Vielen Dank - wir melden uns innerhalb von 24 Stunden.</p>
                ) : (
                  <button className="send-btn" onClick={submitContact} disabled={isLoading}>
                    {isLoading ? "Wird gesendet..." : "Beratung anfragen"}
                  </button>
                )}
              </div>
            )}
          </aside>
        </section>
      </main>
      <Footer />
    </>
  );
}
